import AntIcon from "@/components/icons/AntIcon";
import { selectTagsStandardProps } from "@/utils/componentUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validations } from "@/utils/validationUtils";
import { Button, Card, Col, Form, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { rowGutter } from "../../../../../common/constants";
import { ClientFilterPageRequest } from "../../../../client/types";
import { AgentSelect } from "../../../../enumerations/components/form/AgentSelect";
import InstitutionSelect from "../../../../enumerations/components/form/InstitutionSelect";
import { InstitutionType } from "../../../../institution/enums";
import { CalcType, calcTypeTMap } from "../../../enums";
import { CalcTypeTag } from "../../../records/components/CalcTypeTag";
import { DEFAULT_FILTER_CALC_BLACK_LISTS_QUERY_PARAMS } from "../../api";
import { CalcBlacklistFilterPageRequest } from "../../types";

const colSpan = 6;

interface Props {
  searchParams: CalcBlacklistFilterPageRequest;
  onCreateClick: () => void;
}

export const CalcBlacklistFilterForm = ({ searchParams, onCreateClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form] = Form.useForm<CalcBlacklistFilterPageRequest>();

  useEffect(() => {
    form.setFieldsValue(searchParams);
  }, [searchParams, form]);

  const onFilterSubmit = (values: ClientFilterPageRequest) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...values,
            ...skipInitialFormValues(values, DEFAULT_FILTER_CALC_BLACK_LISTS_QUERY_PARAMS),
            pageIndex: undefined,
          },
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("calc.blacklist.titles.pageTitle")}</h2>}
      extra={
        <Button type="primary" icon={<AntIcon type="plus" />} onClick={onCreateClick}>
          {t("calc.blacklist.actions.createBlacklist")}
        </Button>
      }
    >
      <Form form={form} onFinish={onFilterSubmit} layout="vertical" name="calcBlacklistSearchForm">
        <Row gutter={rowGutter} justify="center">
          <Col span={colSpan}>
            <Form.Item name="calcType" label={t("calc.enums.calcType._label")} rules={[validations.none]}>
              <Select
                {...selectTagsStandardProps(calcTypeTMap)}
                allowClear
                options={Object.values(CalcType).map((calcType) => ({
                  value: calcType,
                  label: <CalcTypeTag type={calcType} />,
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <InstitutionSelect
              formItemProps={{
                name: "institutionIds",
                label: t("common.insuranceInstitutions"),
                rules: [validations.none],
              }}
              selectProps={{ mode: "multiple", maxTagCount: "responsive", allowClear: true }}
              optionsProps={{
                includeDeactivated: true,
                filterType: InstitutionType.INSURANCE_COMPANY,
              }}
            />
          </Col>

          <Col span={colSpan}>
            <Form.Item name="agentIds" label={t("common.agents")} rules={[validations.none]}>
              <AgentSelect allowClear isMultiple />
            </Form.Item>
          </Col>

          <Col flex="110px" className="no-title-space">
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
                {t("common.filter")}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};
